import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api } from "../api";
import type { CaseRecord, RecordAttachment } from "../types";
import { SOURCE_TYPE_LABELS } from "../types";
import type { LedgerRow } from "../lib/reimbursementSummary";
import { formatDate } from "../lib/formatDate";
import { AttachmentCard, attachmentKind } from "./AttachmentCard";
import ZoomableLightbox from "./ZoomableLightbox";

// Inline source side panel for the Financials triage queue. Composes the
// already-built pieces — the deterministic source resolver (row.source /
// row.sources), the inline PDF/image viewer (AttachmentCard), and the
// lightbox (ZoomableLightbox) — beside the table so a row's status can be
// set against its evidence without leaving the page.
//
// Two render modes, toggled in-panel when the source HAS an attachment:
//   - "Extracted text" (default): the source text with the verbatim quote(s)
//     highlighted. For an attachment source that text is the attachment's own
//     extracted/OCR text (where the quote lives) — fetched lazily via the same
//     api.getAttachment AttachmentCard uses; for a record-only source it's the
//     record body/OCR (already in hand).
//   - "Original document": the real PDF/image rendered inline via AttachmentCard.
// The toggle is per-row (resets to text each row open via the key at the call
// site); only the panel WIDTH persists.
//
// Genuinely new here is just the HighlightedBody render path, the view-mode
// toggle, and the lazy attachment-text fetch (reusing api.getAttachment).

type ViewMode = "text" | "doc";

interface Props {
  caseId: string;
  records: CaseRecord[];
  row: LedgerRow;
  // Every verbatim quote backing this row (>1 when facts merged into the row).
  quotes: string[];
  onClose: () => void;
  // The existing navigate-away deep path (jump → records tab + back bar),
  // kept available for deep viewing alongside the inline panel.
  onOpenFullRecord: () => void;
}

function resolveAttachment(
  record: CaseRecord | null,
  attachmentId: string | null,
): RecordAttachment | null {
  if (!record || !attachmentId) return null;
  return (record.attachments ?? []).find((a) => a.id === attachmentId) ?? null;
}

// Merge overlapping/adjacent [start,end) ranges so a body span matched by
// two quotes isn't double-wrapped.
function mergeRanges(ranges: Array<[number, number]>): Array<[number, number]> {
  const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
  const out: Array<[number, number]> = [];
  for (const [s, e] of sorted) {
    const last = out[out.length - 1];
    if (last && s <= last[1]) last[1] = Math.max(last[1], e);
    else out.push([s, e]);
  }
  return out;
}

// Render `text` with every occurrence of any quote wrapped in <mark>. Quotes
// are exact substrings of the source body (the extraction pipeline's
// verify-or-discard invariant guarantees it), so a plain indexOf scan is
// sufficient — no fuzzy matching, no AI.
function HighlightedBody({ text, quotes }: { text: string; quotes: string[] }) {
  const firstMarkRef = useRef<HTMLElement | null>(null);

  const ranges = useMemo(() => {
    const found: Array<[number, number]> = [];
    for (const q of quotes) {
      const needle = q.trim();
      if (needle.length < 3) continue;
      let from = 0;
      for (;;) {
        const idx = text.indexOf(needle, from);
        if (idx === -1) break;
        found.push([idx, idx + needle.length]);
        from = idx + needle.length;
      }
    }
    return mergeRanges(found);
  }, [text, quotes]);

  // Bring the first highlight into view when the source/quotes change.
  useEffect(() => {
    firstMarkRef.current?.scrollIntoView({ block: "center" });
  }, [text, quotes]);

  if (ranges.length === 0) {
    return <div className="src-panel-body-text">{text}</div>;
  }

  const parts: ReactNode[] = [];
  let cursor = 0;
  let markN = 0;
  for (const [s, e] of ranges) {
    if (s > cursor) parts.push(text.slice(cursor, s));
    const isFirst = markN === 0;
    parts.push(
      <mark
        key={`m-${s}`}
        className="src-panel-quote-hit"
        ref={isFirst ? firstMarkRef : undefined}
      >
        {text.slice(s, e)}
      </mark>,
    );
    markN += 1;
    cursor = e;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));

  return <div className="src-panel-body-text">{parts}</div>;
}

export default function SourcePanel({
  caseId,
  records,
  row,
  quotes,
  onClose,
  onOpenFullRecord,
}: Props) {
  const [view, setView] = useState<ViewMode>("text");
  const [lightbox, setLightbox] = useState<{ url: string; filename: string } | null>(null);
  // Lazily-loaded extracted/OCR text of the source ATTACHMENT (the record body
  // isn't where an attachment's quote lives). "idle" until first needed.
  const [attachText, setAttachText] = useState<string | null>(null);
  const [attachTextState, setAttachTextState] = useState<"idle" | "loading" | "error" | "done">("idle");

  const record = useMemo(
    () => records.find((r) => r.id === row.source?.recordId) ?? null,
    [records, row.source?.recordId],
  );
  const attachment = resolveAttachment(record, row.source?.attachmentId ?? null);
  const hasAttachment = !!attachment;
  const recordText = record?.body_text ?? record?.ocr_text ?? null;

  // Fetch the attachment's extracted text once (reuses the call AttachmentCard
  // makes for its preview). Only when the attachment actually has text.
  useEffect(() => {
    if (!attachment || !attachment.has_text) return;
    let cancelled = false;
    setAttachTextState("loading");
    (async () => {
      try {
        const { attachment: full } = await api.getAttachment(attachment.id);
        if (cancelled) return;
        setAttachText(full.extracted_text ?? null);
        setAttachTextState("done");
      } catch {
        if (!cancelled) setAttachTextState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attachment?.id, attachment?.has_text]);

  const typeLabel =
    record && record.source_type in SOURCE_TYPE_LABELS
      ? SOURCE_TYPE_LABELS[record.source_type]
      : null;
  const dateLabel = formatDate(record?.record_date ?? row.dateRaw, "short");

  // Unambiguous source-type line so that when the toggle is ABSENT it's clearly
  // because there's no attachment — not because the toggle is broken.
  const sourceTypeLabel = (): string => {
    if (attachment) {
      const k = attachmentKind(attachment.mime_type);
      const kindWord = k === "pdf" ? "PDF" : k === "image" ? "Image" : "Document";
      return `Source: ${kindWord} attachment — ${attachment.filename}`;
    }
    if (record) {
      return `Source: ${typeLabel ? `${typeLabel.toLowerCase()} ` : ""}body (no attachment)`;
    }
    return "Source: not found";
  };

  // The extracted-text body for the current source.
  const textBody = (): ReactNode => {
    if (hasAttachment) {
      if (!attachment!.has_text) {
        return (
          <p className="rs-empty">
            No extracted text for this attachment — switch to the original document.
          </p>
        );
      }
      if (attachTextState === "loading" || attachTextState === "idle") {
        return <p className="rs-empty">Loading extracted text…</p>;
      }
      if (attachTextState === "error") {
        return (
          <p className="rs-empty">
            Couldn’t load the extracted text — switch to the original document.
          </p>
        );
      }
      return attachText ? (
        <HighlightedBody text={attachText} quotes={quotes} />
      ) : (
        <p className="rs-empty">No extracted text — switch to the original document.</p>
      );
    }
    if (record) {
      return recordText ? (
        <HighlightedBody text={recordText} quotes={quotes} />
      ) : (
        <p className="rs-empty">This record has no text content to show.</p>
      );
    }
    return <p className="rs-empty">Source record not found.</p>;
  };

  return (
    <aside className="src-panel" aria-label="Row source">
      <header className="src-panel-head">
        <div className="src-panel-head-meta">
          <div className="src-panel-head-line">
            {dateLabel && <span className="src-panel-date">{dateLabel}</span>}
            {typeLabel && <span className="src-panel-chip">{typeLabel}</span>}
            {row.party && <span className="src-panel-party">{row.party}</span>}
          </div>
          {(record?.content_label || record?.subject) && (
            <div
              className="src-panel-subject"
              title={record?.subject ?? undefined}
            >
              {record?.content_label || record?.subject}
            </div>
          )}
          {record?.content_label && record?.subject &&
            record.subject.trim() !== record.content_label.trim() && (
              <div className="src-panel-original-subject" title={record.subject}>
                {record.subject}
              </div>
            )}
          <div className={`src-panel-srctype${hasAttachment ? " has-doc" : ""}`} title={attachment?.filename}>
            <span aria-hidden>{hasAttachment ? "📎" : "✉"}</span> {sourceTypeLabel()}
          </div>
        </div>
        <button
          type="button"
          className="src-panel-close"
          onClick={onClose}
          aria-label="Close source panel"
          title="Close"
        >
          ✕
        </button>
      </header>

      {/* Toggle only when there's a document to toggle TO. Record-only sources
          (e.g. an email body) have nothing to switch to — no dead control. */}
      {hasAttachment && (
        <div className="src-panel-toggle" role="tablist" aria-label="Source view">
          <button
            type="button"
            role="tab"
            aria-selected={view === "text"}
            className={`src-panel-toggle-btn${view === "text" ? " active" : ""}`}
            onClick={() => setView("text")}
          >
            Extracted text
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === "doc"}
            className={`src-panel-toggle-btn${view === "doc" ? " active" : ""}`}
            onClick={() => setView("doc")}
          >
            Original document
          </button>
        </div>
      )}

      <div className="src-panel-actions">
        <button type="button" className="rs-link" onClick={onOpenFullRecord}>
          Open full record ↗
        </button>
      </div>

      {quotes.length > 0 && (
        <div className="src-panel-quotes">
          <div className="src-panel-label">
            {quotes.length === 1 ? "Extracted quote" : `Extracted quotes (${quotes.length})`}
          </div>
          {quotes.map((q, i) => (
            <blockquote key={`q-${i}`} className="src-panel-quote">
              “{q}”
            </blockquote>
          ))}
        </div>
      )}

      <div className="src-panel-source">
        {hasAttachment && view === "doc" ? (
          <AttachmentCard
            caseId={caseId}
            attachment={attachment!}
            onOpenLightbox={(url, filename) => setLightbox({ url, filename })}
            autoExpand
          />
        ) : (
          <>
            <div className="src-panel-label">In context</div>
            {textBody()}
          </>
        )}
      </div>

      {lightbox && (
        <ZoomableLightbox
          src={lightbox.url}
          filename={lightbox.filename}
          onClose={() => setLightbox(null)}
        />
      )}
    </aside>
  );
}
